import React, { useState } from "react";
import {
  FaClock,
  FaCopy,
  FaDesktop,
  FaDollarSign,
  FaUndo,
  FaUserClock,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { buildRoiForecast, calculateRoi } from "../calculators";
import { CLIENT_TYPES, ROI_BENCHMARKS, ROI_TASKS } from "../assumptions";
import { useClientOverview } from "../ClientOverviewContext";
import {
  buildRoiBase,
  missingRoiInputs,
  resolveRoiInputs,
  toRoiInputs,
} from "../roiSelectors";
import {
  buildRoiSummary,
  formatMoney,
  formatNumber,
  formatPercent,
} from "../selectors";
import { Metric } from "./Metric";
import { NumberField } from "./NumberField";
import { ForecastChart, ForecastTable, TaskMinutesChart, ValueVsCostChart } from "./RoiCharts";
import { RoiPresentation } from "./RoiPresentation";
import { SourceTag } from "./SourceTag";

function RoiResults({ values, result }) {
  const netIsPositive = result.monthlyNetSavings >= 0;
  const forecast = buildRoiForecast(result);

  return (
    <>
      <section className="pl-metrics" aria-label="ROI results">
        <Metric
          icon={FaClock}
          label="Time saved per loan"
          value={`${formatNumber(result.minutesSavedPerLoan, 1)} min`}
          hint={`${formatPercent(result.timeReduction)} less hands-on time, worth ${formatMoney(
            result.savingsPerLoan,
            { cents: true },
          )}`}
        />
        <Metric
          icon={FaUserClock}
          label="Staff hours back per month"
          value={formatNumber(result.monthlyHoursSaved, 0)}
          hint={`${formatNumber(result.fteFreed, 1)} full-time people · ${formatNumber(
            result.annualHoursSaved,
          )} h a year`}
          tone="violet"
        />
        <Metric
          icon={FaDollarSign}
          label="Labor saved per year"
          value={formatMoney(result.annualLaborSavings)}
          hint={`${formatMoney(result.monthlyLaborSavings)} a month at ${formatMoney(
            Number(values.hourlyCost) || 0,
          )}/h`}
          tone="green"
        />
        <Metric
          icon={FaDollarSign}
          label="Net savings per year"
          value={formatMoney(result.annualNetSavings)}
          hint={`After ${formatMoney(result.annualAddyCost)} of Addy a year`}
          tone={netIsPositive ? "green" : "rose"}
        />
        <Metric
          icon={FaDollarSign}
          label="Return on Addy"
          value={result.roiMultiple === null ? "—" : `${formatNumber(result.roiMultiple, 2)}×`}
          hint={
            result.roiMultiple === null
              ? "Enter a price to compare"
              : `${formatMoney(result.roiMultiple, { cents: true })} of staff time back per $1`
          }
          tone={result.roiMultiple !== null && result.roiMultiple < 1 ? "rose" : "accent"}
        />
        <Metric
          icon={FaClock}
          label="Turn time"
          value={
            result.turnTimeDaysSaved
              ? `${formatNumber(result.turnTimeDaysSaved, 1)} days faster`
              : "Not measured"
          }
          hint="Per loan, from doc check to submission."
          tone="amber"
        />
      </section>

      <div className="pl-roi-charts">
        <section className="pl-card pl-card--pad">
          <h3 className="pl-section-title">Staff time saved against what Addy costs</h3>
          <div className="pl-roi-value">
            <div className="pl-roi-hero">
              <span className="pl-result-label">Return on Addy</span>
              <span className="pl-roi-hero-value">
                {result.roiMultiple === null ? "—" : `${formatNumber(result.roiMultiple, 2)}×`}
              </span>
              <span className="pl-field-hint">Staff time saved ÷ Addy cost, per year</span>
            </div>
            <div className="pl-roi-value-chart">
              <ValueVsCostChart result={result} />
            </div>
          </div>
        </section>

        <section className="pl-card pl-card--pad">
          <h3 className="pl-section-title">Hands-on minutes per loan, by task</h3>
          <TaskMinutesChart result={result} />
        </section>

        <section className="pl-card pl-card--pad">
          <h3 className="pl-section-title">Three-year outlook</h3>
          <ForecastChart forecast={forecast} />
          <details className="pl-chart-table">
            <summary>Show the numbers</summary>
            <ForecastTable forecast={forecast} />
          </details>
        </section>
      </div>
    </>
  );
}

/**
 * Client-facing ROI: how much hands-on time Addy gives back per loan, what
 * that is worth, and how it compares with the client's Addy bill. HubSpot
 * fills in what it knows about the picked client; the client's own numbers
 * are typed in, never assumed.
 */
export function RoiCalculator({ pilots, state, onChange }) {
  const [presenting, setPresenting] = useState(false);
  const pilot = pilots.find((entry) => entry.dealId === state.pilotId) || null;
  const overview = useClientOverview(pilot?.organizationId || null);
  const base = buildRoiBase({ pilot, organizationName: overview.data?.name });
  const { values, sources } = resolveRoiInputs({ base, state });
  const result = calculateRoi(toRoiInputs(values));
  const missing = missingRoiInputs(values, result);
  const isReady = missing.length === 0;
  const clientType = CLIENT_TYPES.find((type) => type.key === values.clientType);
  const running = pilots.filter((entry) => !entry.isClosed);
  const past = pilots.filter((entry) => entry.isClosed);

  const set = (key, value) => onChange({ ...state, edits: { ...state.edits, [key]: value } });
  const setTask = (taskKey, fieldName, value) =>
    onChange({
      ...state,
      tasks: { ...state.tasks, [taskKey]: { ...state.tasks[taskKey], [fieldName]: value } },
    });
  const keepPreparedBy = () =>
    "preparedBy" in state.edits ? { preparedBy: state.edits.preparedBy } : {};
  const field = (key) => ({
    id: `pl-roi-${key}`,
    value: values[key],
    source: sources[key],
    onChange: (value) => set(key, value),
  });

  const typeLabel = clientType?.label || "Client";
  const clientName = String(values.clientName || "").trim();
  const summaryTitle = clientName ? `${clientName} (${typeLabel})` : typeLabel;
  const notReadyTitle = isReady ? undefined : `Still needed: ${missing.join(", ")}`;

  const copySummary = async () => {
    try {
      await navigator.clipboard.writeText(buildRoiSummary(summaryTitle, values, result));
      toast.success("ROI summary copied");
    } catch (error) {
      toast.error("Could not copy — your browser blocked clipboard access.");
    }
  };

  return (
    <div className="pl-calc">
      <div className="pl-roi-grid">
        <section className="pl-card pl-card--pad">
          <header className="pl-calc-head">
            <div>
              <h3>The client</h3>
              <p>Pick a HubSpot pilot to fill in what HubSpot knows. The rest comes from the client.</p>
            </div>
          </header>
          <div className="pl-fields">
            <div className="pl-field">
              <label className="pl-field-label" htmlFor="pl-roi-pilot">
                Client
              </label>
              <select
                id="pl-roi-pilot"
                className="pl-select"
                value={pilot ? pilot.dealId : ""}
                onChange={(event) =>
                  onChange({ pilotId: event.target.value, edits: keepPreparedBy(), tasks: {} })
                }
              >
                <option value="">Someone else (type the details)</option>
                {running.length ? (
                  <optgroup label="Running pilots">
                    {running.map((entry) => (
                      <option key={entry.dealId} value={entry.dealId}>
                        {entry.name}
                      </option>
                    ))}
                  </optgroup>
                ) : null}
                {past.length ? (
                  <optgroup label="Past pilots">
                    {past.map((entry) => (
                      <option key={entry.dealId} value={entry.dealId}>
                        {entry.name}
                      </option>
                    ))}
                  </optgroup>
                ) : null}
              </select>
            </div>
            <div className="pl-field">
              <span className="pl-field-top">
                <label className="pl-field-label" htmlFor="pl-roi-clientType">
                  Client type
                </label>
                <SourceTag source={sources.clientType} />
              </span>
              <select
                id="pl-roi-clientType"
                className="pl-select"
                value={values.clientType}
                onChange={(event) => set("clientType", event.target.value)}
              >
                <option value="">Pick a type</option>
                {CLIENT_TYPES.map((type) => (
                  <option key={type.key} value={type.key}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
            <NumberField
              {...field("loansPerMonth")}
              label="Loans closed per month"
              placeholder="Ask the client"
            />
            <NumberField
              {...field("hourlyCost")}
              label="Staff cost per hour"
              prefix="$"
              placeholder="Ask the client"
              hint={`Fully loaded processor / underwriter cost. For reference: ${ROI_BENCHMARKS.hourlyCost}`}
            />
            <NumberField
              {...field("turnTimeDaysSaved")}
              label="Turn time saved per loan"
              suffix="days"
              step={0.5}
              placeholder="Not measured"
              hint={`For reference: ${ROI_BENCHMARKS.turnTime}`}
            />
          </div>

          <fieldset className="pl-pricing">
            <legend className="pl-field-label">Addy pricing</legend>
            <div className="pl-segment" role="group" aria-label="Addy pricing model">
              <button
                type="button"
                className={values.pricingModel === "perLoan" ? "is-active" : ""}
                aria-pressed={values.pricingModel === "perLoan"}
                onClick={() => set("pricingModel", "perLoan")}
              >
                Per funded loan
              </button>
              <button
                type="button"
                className={values.pricingModel === "perSeat" ? "is-active" : ""}
                aria-pressed={values.pricingModel === "perSeat"}
                onClick={() => set("pricingModel", "perSeat")}
              >
                Per seat
              </button>
            </div>
            <div className="pl-fields">
              {values.pricingModel === "perSeat" ? (
                <>
                  <NumberField
                    {...field("pricePerSeat")}
                    label="Price per seat per month"
                    prefix="$"
                  />
                  <NumberField
                    {...field("seats")}
                    label="Seats"
                    placeholder="How many users"
                    hint={sources.seats === "hubspot" ? "Loan officers at the company, from HubSpot." : null}
                  />
                </>
              ) : (
                <NumberField
                  {...field("pricePerLoan")}
                  label="Price per funded loan"
                  prefix="$"
                  hint="Volume pricing goes down to $35 at 300+ loans a month."
                />
              )}
            </div>
          </fieldset>

          <button
            type="button"
            className="pl-btn pl-btn--ghost"
            onClick={() => onChange({ ...state, edits: keepPreparedBy(), tasks: {} })}
          >
            <FaUndo aria-hidden="true" /> Clear what I typed
          </button>
        </section>

        <section className="pl-card pl-card--pad">
          <header className="pl-calc-head">
            <div>
              <h3>Hands-on minutes per loan</h3>
              <p>The client's own minutes. Leave a task empty when they do not do it.</p>
            </div>
          </header>
          <div className="pl-table-wrap">
            <table className="pl-table pl-task-table">
              <thead>
                <tr>
                  <th className="pl-left">Task</th>
                  <th>Today</th>
                  <th>With Addy</th>
                  <th>Saved</th>
                </tr>
              </thead>
              <tbody>
                {result.tasks.map((task, index) => {
                  const label = ROI_TASKS[index].label;

                  return (
                    <tr key={task.key}>
                      <td className="pl-left">
                        {label}
                        <span className="pl-task-ref">{ROI_BENCHMARKS[task.key]}</span>
                      </td>
                      <td>
                        <input
                          className="pl-task-input"
                          type="number"
                          min={0}
                          placeholder="min"
                          aria-label={`${label}: minutes today`}
                          value={values.tasks[task.key].minutesToday}
                          onChange={(event) => setTask(task.key, "minutesToday", event.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          className="pl-task-input"
                          type="number"
                          min={0}
                          placeholder="min"
                          aria-label={`${label}: minutes with Addy`}
                          value={values.tasks[task.key].minutesWithAddy}
                          onChange={(event) =>
                            setTask(task.key, "minutesWithAddy", event.target.value)
                          }
                        />
                      </td>
                      <td className="pl-strong">{formatNumber(task.minutesSaved, 1)} min</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td className="pl-left">Per loan</td>
                  <td>{formatNumber(result.minutesTodayPerLoan, 1)} min</td>
                  <td>{formatNumber(result.minutesWithAddyPerLoan, 1)} min</td>
                  <td className="pl-strong">{formatNumber(result.minutesSavedPerLoan, 1)} min</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <p className="pl-field-hint">
            The grey notes are what clients told us on calls. They are for reference only and are
            not used unless you type them in.
          </p>
        </section>
      </div>

      {isReady ? (
        <RoiResults values={values} result={result} />
      ) : (
        <section className="pl-card pl-card--pad pl-roi-empty" role="status">
          <h3>Add the client's numbers to see their ROI</h3>
          <p>
            Still needed: {missing.join(", ")}. Nothing here is pre-filled with made-up numbers:
            HubSpot fills in the client type and loan officers, Addy's list prices fill in the
            price, and everything else comes from the client.
          </p>
        </section>
      )}

      <section className="pl-card pl-card--pad pl-present">
        <div className="pl-fields">
          <div className="pl-field">
            <span className="pl-field-top">
              <label className="pl-field-label" htmlFor="pl-roi-clientName">
                Client name (for the presentation)
              </label>
              <SourceTag source={sources.clientName} />
            </span>
            <input
              id="pl-roi-clientName"
              className="pl-text-input"
              value={values.clientName}
              placeholder="e.g. Saxton Mortgage"
              onChange={(event) => set("clientName", event.target.value)}
            />
          </div>
          <div className="pl-field">
            <label className="pl-field-label" htmlFor="pl-roi-preparedBy">
              Prepared by
            </label>
            <input
              id="pl-roi-preparedBy"
              className="pl-text-input"
              value={values.preparedBy}
              placeholder="Your name"
              onChange={(event) => set("preparedBy", event.target.value)}
            />
          </div>
        </div>
        <div className="pl-roi-actions">
          <button
            type="button"
            className="pl-btn"
            onClick={copySummary}
            disabled={!isReady}
            title={notReadyTitle}
          >
            <FaCopy aria-hidden="true" /> Copy summary
          </button>
          <button
            type="button"
            className="pl-btn pl-btn--solid"
            onClick={() => setPresenting(true)}
            disabled={!isReady}
            title={notReadyTitle}
          >
            <FaDesktop aria-hidden="true" /> Present to client
          </button>
        </div>
      </section>

      {presenting && isReady ? (
        <RoiPresentation
          clientName={clientName}
          preparedBy={String(values.preparedBy || "")}
          clientTypeLabel={typeLabel}
          inputs={values}
          result={result}
          forecast={buildRoiForecast(result)}
          onClose={() => setPresenting(false)}
        />
      ) : null}
    </div>
  );
}
